import { useCallback, useEffect, type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';

import { useApprovals } from './ApprovalsContext.tsx';
import { openApprovalsPaneForContext } from './approvalsPaneSidebar.ts';

const SOURCE_CONFLUENCE_APPROVALS_PARAM = 'source_confluence_approvals';

const i18n = defineMessages({
	alreadyApprovedTitle: {
		id: 'approvals.flag.already-approved.title',
		defaultMessage: "You've already approved this page",
		description:
			'Title for the info flag shown when a reviewer clicks an approval notification but has already approved the page',
	},
	alreadyRequestedChangesTitle: {
		id: 'approvals.flag.already-requested-changes.title',
		defaultMessage: "You've already requested changes on this page",
		description:
			'Title for the info flag shown when a reviewer clicks an approval notification but has already requested changes on the page',
	},
	changeDecisionAction: {
		id: 'approvals.flag.already-responded.change-decision',
		defaultMessage: 'Change your decision',
		description:
			'Action button label on the already-responded info flag that opens the approvals pane so the reviewer can change their decision',
	},
	approvalUnavailableTitle: {
		id: 'approvals.flag.approval-unavailable.title',
		defaultMessage: 'This approval request is no longer available',
		description:
			'Title for the warning flag shown when an approval request is no longer available (e.g. it was deleted or the user was removed as a reviewer)',
	},
	approvalUnavailableDescription: {
		id: 'approvals.flag.approval-unavailable.description',
		defaultMessage: 'It may have been deleted.',
		description:
			'Description for the warning flag shown when an approval request is no longer available',
	},
});

type ApprovalCtaState = 'none' | 'unavailable' | 'already-responded';

const removeSourceConfluenceApprovalsParam = (): void => {
	const urlParams = new URLSearchParams(window.location.search);
	urlParams.delete(SOURCE_CONFLUENCE_APPROVALS_PARAM);
	const newSearch = urlParams.size > 0 ? `?${urlParams.toString()}` : '';
	const newUrl = `${window.location.pathname}${newSearch}${window.location.hash}`;
	window.history.replaceState({}, '', newUrl);
};

const ApprovalCtaFlagComponent: FC<WithFlagsProps> = ({ flags }) => {
	const { formatMessage } = useIntl();
	const {
		approvalRequestLoading,
		isCurrentUserApprover,
		currentUserApprover,
		approvalRequest,
		contentId,
		contentStatus,
	} = useApprovals();
	const rightSidebarContext = useRightSidebarContext();

	const openApprovalsPane = useCallback(() => {
		openApprovalsPaneForContext({ contentId, contentStatus, rightSidebarContext });
	}, [contentId, contentStatus, rightSidebarContext]);

	useEffect(() => {
		if (approvalRequestLoading) {
			return;
		}

		const urlParams = new URLSearchParams(window.location.search);
		const isFromApprovalsCta = urlParams.has(SOURCE_CONFLUENCE_APPROVALS_PARAM);

		if (!isFromApprovalsCta) {
			return;
		}

		removeSourceConfluenceApprovalsParam();

		let state: ApprovalCtaState = 'none';
		if (!isCurrentUserApprover) {
			state = 'unavailable';
		} else if (currentUserApprover && currentUserApprover.status !== 'PENDING_REVIEW') {
			state = 'already-responded';
		}

		if (state === 'unavailable') {
			void flags.showWarningFlag({
				title: formatMessage(i18n.approvalUnavailableTitle),
				description: formatMessage(i18n.approvalUnavailableDescription),
				isAutoDismiss: true,
			});
			return;
		}

		if (state === 'already-responded' && currentUserApprover) {
			const isApprovalInProgress = approvalRequest?.review?.reviewStatus === 'PENDING';
			const title =
				currentUserApprover.status === 'REVIEWER_APPROVED'
					? formatMessage(i18n.alreadyApprovedTitle)
					: formatMessage(i18n.alreadyRequestedChangesTitle);

			void flags.showInfoFlag({
				title,
				isAutoDismiss: true,
				...(isApprovalInProgress && {
					actions: [
						{
							content: formatMessage(i18n.changeDecisionAction),
							onClick: openApprovalsPane,
						},
					],
				}),
			});
		}
	}, [
		approvalRequestLoading,
		isCurrentUserApprover,
		currentUserApprover,
		approvalRequest,
		flags,
		formatMessage,
		openApprovalsPane,
	]);

	return null;
};

/**
 * ApprovalCtaFlag
 *
 * Handles all flag logic for users arriving via an approvals notification CTA
 * (identified by the `source_confluence_approvals` query parameter).
 *
 * Three states are detected (in priority order):
 *
 * 1. **Unavailable** — the user is no longer an active reviewer (request cancelled or user
 *    removed). Shows a warning flag: "This approval request is no longer available."
 *
 * 2. **Already responded** — the user is still a reviewer but has already approved or
 *    requested changes. Shows an info flag with a status-specific title:
 *    - "You've already approved this page"
 *    - "You've already requested changes on this page"
 *    If the approval is still in progress, a "Change your decision" action button is added.
 *
 * 3. **None** — the user is still a pending reviewer; no flag is needed.
 *
 * The query param is always removed after being read to prevent duplicate flags.
 *
 * This component renders nothing to the DOM (`null`) and is SSR-safe: the flag logic only
 * runs inside a `useEffect` (client-side only), so it will not cause hydration mismatches.
 */
export const ApprovalCtaFlag = withFlags(ApprovalCtaFlagComponent);
